import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/layouts/DashboardLayout";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ChangePassword from "@/pages/ChangePassword";
import AuditLogs from "@/pages/admin/AuditLogs";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import UsersAdmin from "@/pages/admin/UsersAdmin";
import Courses from "@/pages/admin/Courses";
import Batches from "@/pages/admin/Batches";
import AssessmentsAdmin from "@/pages/admin/AssessmentsAdmin";
import Reports from "@/pages/admin/Reports";
import Settings from "@/pages/admin/Settings";

// Trainer
import TrainerDashboard from "@/pages/trainer/TrainerDashboard";
import QuestionBank from "@/pages/trainer/QuestionBank";
import CreateTest from "@/pages/trainer/CreateTest";
import AssessmentQuestionsUpload from "@/pages/trainer/AssessmentQuestionsUpload";
import LiveExams from "@/pages/trainer/LiveExams";
import TrainerResults from "@/pages/trainer/TrainerResults";
import Analytics from "@/pages/trainer/Analytics";

// Student
import StudentDashboard from "@/pages/student/StudentDashboard";
import MyCourses from "@/pages/student/MyCourses";
import UpcomingTests from "@/pages/student/UpcomingTests";
import ExamIntro from "@/pages/student/ExamIntro";
import TakeAssessment from "@/pages/student/TakeAssessment";
import ResultDetail from "@/pages/student/ResultDetail";
import StudentResults from "@/pages/student/StudentResults";
import Certificates from "@/pages/student/Certificates";

// Shared
import Profile from "@/pages/Profile";

function RoleRedirect() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return <Navigate to={`/${user.role}`} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RoleRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/change-password" element={<ProtectedRoute allowMustChange><ChangePassword /></ProtectedRoute>} />
      <Route path="/app" element={<RoleRedirect />} />

      {/* Admin */}
      <Route element={<ProtectedRoute roles={["admin"]}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/admins" element={<UsersAdmin role="admin" title="Admins" subtitle="Manage administrator accounts who can run the institute." testIdPrefix="admin" />} />
        <Route path="/admin/students" element={<UsersAdmin role="student" title="Employees" subtitle="Manage employee accounts and enrollments." testIdPrefix="student" />} />
        <Route path="/admin/trainers" element={<UsersAdmin role="trainer" title="Trainers" subtitle="Manage trainer accounts." testIdPrefix="trainer" />} />
        <Route path="/admin/courses" element={<Courses />} />
        <Route path="/admin/batches" element={<Batches />} />
        <Route path="/admin/assessments" element={<AssessmentsAdmin />} />
        <Route path="/admin/reports" element={<Reports />} />
        <Route path="/admin/audit-logs" element={<AuditLogs />} />
        <Route path="/admin/settings" element={<Settings />} />
        <Route path="/admin/profile" element={<Profile />} />
      </Route>

      {/* Trainer */}
      <Route element={<ProtectedRoute roles={["trainer"]}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/trainer" element={<TrainerDashboard />} />
        <Route path="/trainer/questions" element={<QuestionBank />} />
        <Route path="/trainer/create-test" element={<CreateTest />} />
        <Route path="/trainer/assessments/:id/questions" element={<AssessmentQuestionsUpload />} />
        <Route path="/trainer/live-exams" element={<LiveExams />} />
        <Route path="/trainer/results" element={<TrainerResults />} />
        <Route path="/trainer/reports" element={<Reports />} />
        <Route path="/trainer/analytics" element={<Analytics />} />
        <Route path="/trainer/profile" element={<Profile />} />
      </Route>

      {/* Student dashboard pages */}
      <Route element={<ProtectedRoute roles={["student"]}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/student" element={<StudentDashboard />} />
        <Route path="/student/courses" element={<MyCourses />} />
        <Route path="/student/upcoming" element={<UpcomingTests />} />
        <Route path="/student/results" element={<StudentResults />} />
        <Route path="/student/certificates" element={<Certificates />} />
        <Route path="/student/profile" element={<Profile />} />
      </Route>

      {/* Student exam (full-screen, no layout) */}
      <Route path="/student/exam/:id/intro" element={<ProtectedRoute roles={["student"]}><ExamIntro /></ProtectedRoute>} />
      <Route path="/student/exam/:id/take" element={<ProtectedRoute roles={["student"]}><TakeAssessment /></ProtectedRoute>} />
      <Route path="/student/result/:id" element={<ProtectedRoute roles={["student"]}><ResultDetail /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
